import type {
  ChiTietChinhSachChietKhau,
  ChinhSachChietKhau,
  DonVi,
  GiaBanDetailRow,
  GiaBanV2,
  LoaiChietKhau
} from '@/types/schemas';
import { type DataTableColumn, FormField, type GridRenderCellParams } from '@/components/custom/erp';
import { loaiGiaBanSearchColumns } from '@/constants/search-columns';
import { formatDate, formatMoney } from '@/lib/formatUtils';
import QUERY_KEYS from '@/constants/query-keys';

export const getDataTableColumns = (): DataTableColumn<GiaBanV2>[] => [
  {
    field: 'ma_vt',
    headerName: 'Mã vật tư',
    width: 150,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => params.row.vat_tu_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên vật tư',
    width: 250,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.vat_tu_data?.ten_vt;
    }
  },
  {
    field: 'dvt',
    headerName: 'Đơn vị tính',
    width: 120,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.dvt_data?.dvt;
    }
  },
  {
    field: 'ngay_hl',
    headerName: 'Ngày hiệu lực',
    width: 150,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return formatDate(params.value.split('T')[0]);
    }
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 150,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.ma_kh_data?.customer_code;
    }
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.ma_kh_data?.customer_name;
    }
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá bán',
    width: 150,
    type: 'number',
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => formatMoney(params.row.gia_nt2)
  },
  {
    field: 'thue_yn',
    headerName: 'Bao gồm thuế',
    width: 120,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => (
      <div className='flex h-full items-center justify-center'>
        <input type='checkbox' className='h-4 w-4 accent-gray-500' checked={params.row.thue_yn} disabled />
      </div>
    )
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 100,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.ma_nt_data?.ma_nt;
    }
  },
  {
    field: 'nh_kh1',
    headerName: 'Nhóm khách hàng 1',
    width: 180,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.nh_kh1_data?.ma_nhom;
    }
  },
  {
    field: 'nh_kh2',
    headerName: 'Nhóm khách hàng 2',
    width: 180,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.nh_kh2_data?.ma_nhom;
    }
  },
  {
    field: 'nh_kh3',
    headerName: 'Nhóm khách hàng 3',
    width: 180,
    renderCell: (params: GridRenderCellParams<GiaBanV2>) => {
      return params.row.nh_kh3_data?.ma_nhom;
    }
  }
];

export const ChinhSachChietKhauColumns = (): DataTableColumn<ChinhSachChietKhau>[] => [
  { field: 'ma_chiet_khau', headerName: 'Mã chiết khấu', width: 150 },
  { field: 'ten_chiet_khau', headerName: 'Tên chiết khấu', width: 250 },
  { field: 'loai_chiet_khau', headerName: 'Loại chiết khấu', width: 150 },
  { field: 'ngay_tu', headerName: 'Ngày từ', width: 120 },
  { field: 'ngay_den', headerName: 'Ngày đến', width: 120 },
  { field: 'luy_tien', headerName: 'Lũy tiến', width: 100 },
  { field: 'tru_vao_tien_hang', headerName: 'Trừ vào tiền hàng', width: 150 },
  { field: 'cong_don', headerName: 'Cộng dồn', width: 100 }
];

export const ChiTietChinhSachChietKhauColumns = (): DataTableColumn<ChiTietChinhSachChietKhau>[] => [
  { field: 'ma_chiet_khau', headerName: 'Mã chiết khấu', width: 150 },
  { field: 'ten_chiet_khau', headerName: 'Tên chiết khấu', width: 250 },
  { field: 'phan_loai_xu_ly', headerName: 'Phân loại xử lý', width: 180 },
  { field: 'loai_chiet_khau', headerName: 'Loại chiết khấu', width: 150 },
  { field: 'ngay_tu', headerName: 'Ngày từ', width: 120 },
  { field: 'ngay_den', headerName: 'Ngày đến', width: 120 },
  { field: 'luy_tien', headerName: 'Lũy tiến', width: 100 },
  { field: 'tru_vao_tien_hang', headerName: 'Trừ vào tiền hàng', width: 150 },
  { field: 'cong_don', headerName: 'Cộng dồn', width: 100 }
];

export const loaiGiaBanColumns: DataTableColumn<GiaBanDetailRow>[] = [
  {
    field: 'ma_loai_gb',
    headerName: 'Mã loại giá',
    width: 160,
    renderCell: (params: GridRenderCellParams<GiaBanDetailRow>) => (
      <>
        <FormField
          name='ma_loai_gb'
          type='text'
          value={params.row.ma_loai_gb}
          searchColumns={loaiGiaBanSearchColumns}
          withSearch
          searchEndpoint={`/${QUERY_KEYS.LOAI_GIA_BAN}`}
        />
      </>
    )
  },
  {
    field: 'ten_loai_gb',
    headerName: 'Tên loại giá',
    width: 200,
    renderCell: (params: GridRenderCellParams<GiaBanDetailRow>) => params.row.ten_loai_gb
  },
  {
    field: 'gia',
    headerName: 'Giá',
    width: 150
  }
];

export const loaiCkSearchColumns: DataTableColumn<LoaiChietKhau>[] = [
  { field: 'ma_loai', headerName: 'Mã loại', width: 120 },
  { field: 'ten_loai_chiet_khau', headerName: 'Tên loại chiết khấu', width: 250 },
  { field: 'phan_loai_xu_ly', headerName: 'Phân loại xử lý', width: 180 }
];

export const donViSearchColumns: DataTableColumn<DonVi>[] = [
  { field: 'checkbox', headerName: '', width: 50 },
  { field: 'ma_don_vi', headerName: 'Mã đơn vị', width: 100 },
  { field: 'ten_don_vi', headerName: 'Tên đơn vị', width: 200 },
  { field: 'id', headerName: 'ID', width: 80 }
];
